import React, { useState, useRef, useEffect } from "react";
import { Button, Dropdown, Form, Overlay, OverlayTrigger, Popover, Tooltip } from "react-bootstrap";
import { ArrowUp, FileText, X, List } from "react-feather";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faHistory } from "@fortawesome/free-solid-svg-icons";

import getActivityDateTimeDisplay from "../../utils/datetime";

// Building blocks shared by the provider and practice headers (styles live in _provider-header.scss)

export const CopyChip = ({ icon, label, value, className = "" }) => {
  const [copied, setCopied] = useState(false);

  const handleClick = () => {
    const text = String(value).trim();
    const done = () => {
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    };
    if (navigator.clipboard && navigator.clipboard.writeText) {
      navigator.clipboard.writeText(text).then(done).catch(() => {});
    }
  };

  return (
    <OverlayTrigger
      placement="bottom"
      overlay={<Tooltip>{copied ? "Copied!" : label + " - click to copy"}</Tooltip>}
    >
      <button type="button" className={"ph-copy " + className} onClick={handleClick}>
        {icon}
        <span>{value}</span>
      </button>
    </OverlayTrigger>
  );
};

const CopyPart = ({ text, label, children, className = "ph-specialty-part", placement = "bottom", style }) => {
  const [copied, setCopied] = useState(false);

  const handleClick = () => {
    if (navigator.clipboard && navigator.clipboard.writeText) {
      navigator.clipboard.writeText(String(text).trim()).then(() => {
        setCopied(true);
        setTimeout(() => setCopied(false), 1500);
      }).catch(() => {});
    }
  };

  return (
    <OverlayTrigger
      placement={placement}
      overlay={<Tooltip>{copied ? "Copied!" : "Click to copy" + (label ? " " + label : "")}</Tooltip>}
    >
      <button type="button" className={className} style={style} onClick={handleClick}>{children != null ? children : text}</button>
    </OverlayTrigger>
  );
};

// Inline text that copies itself on click
export const CopyText = ({ text, label, children, placement = "bottom", style }) => {
  if (text == null || String(text).trim() === "") return null;
  return <CopyPart text={text} label={label} className="ph-copy-text" placement={placement} style={style}>{children}</CopyPart>;
};

const STATE_PREFIX = /^[A-Za-z]{2}:\s*/;

// A comma-separated list of IDs; each copies on its own, minus any "ST: " state prefix
export const CopyIds = ({ value, placement }) => {
  if (value == null || String(value).trim() === "") return null;
  const items = String(value).split(",").map((item) => item.trim()).filter((item) => item !== "");
  if (!items.some((item) => STATE_PREFIX.test(item))) {
    return <CopyText text={value} placement={placement} />;
  }
  return (
    <>
      {items.map((item, i) => (
        <React.Fragment key={i}>
          {i > 0 && ", "}
          <CopyText text={item.replace(STATE_PREFIX, "")} placement={placement}>{item}</CopyText>
        </React.Fragment>
      ))}
    </>
  );
};

// The code and the description each copy on their own
export const SpecialtyPill = ({ specialty }) => (
  <span className="ph-specialty">
    <CopyPart text={specialty.code} label="code" />
    <span className="ph-specialty-sep">:</span>
    <CopyPart text={specialty.name} label="description" />
  </span>
);

export const NotesButton = ({ notes }) => {
  const [show, setShow] = useState(false);
  const buttonRef = useRef(null);
  const hasNotes = !!(notes && notes.trim().length > 0);

  return (
    <>
      <Button
        ref={buttonRef}
        size="sm"
        variant="outline-primary"
        className="ph-notes-btn"
        disabled={!hasNotes}
        onClick={() => setShow(!show)}
      >
        <FileText size={14} /> Notes
        {hasNotes && <span className="ph-notes-dot" />}
      </Button>
      <Overlay target={buttonRef.current} show={show && hasNotes} placement="bottom-end" rootClose onHide={() => setShow(false)}>
        <Popover className="ph-notes-popover">
          <Popover.Header className="d-flex justify-content-between align-items-center">
            <span>Notes</span>
            <X size={16} style={{ cursor: "pointer" }} onClick={() => setShow(false)} />
          </Popover.Header>
          <Popover.Body style={{ whiteSpace: "pre-wrap" }}>{notes}</Popover.Body>
        </Popover>
      </Overlay>
    </>
  );
};

export const UpdatedInfo = ({ updated, updatedBy, extra }) => (
  <OverlayTrigger
    placement="bottom"
    overlay={
      <Tooltip>
        <strong>Updated {getActivityDateTimeDisplay(updated)}</strong>
        {updatedBy != null && <div>by {updatedBy}</div>}
        {extra}
      </Tooltip>
    }
  >
    <span className="ph-updated" tabIndex={0}>
      <FontAwesomeIcon icon={faHistory} />
    </span>
  </OverlayTrigger>
);

export const PanelField = ({ label, children }) => (
  <Form.Group className="mb-3">
    <Form.Label className="mb-1">{label}</Form.Label>
    {children}
  </Form.Group>
);

// Dropdown that scrolls to a section of the page. sections: [{ id, label }], where id is a DOM id.
export const SectionNav = ({ sections, compact = false }) => {
  if (!sections || sections.length === 0) {
    return null;
  }

  const scrollTo = (e, id) => {
    e.preventDefault();
    const el = document.getElementById(id);
    if (el) {
      el.scrollIntoView({ behavior: "smooth", block: "start" });
    }
  };

  return (
    <Dropdown align="end">
      <Dropdown.Toggle size="sm" variant="outline-primary" className="ph-notes-btn ph-nav-toggle" title="Jump to section">
        <List size={14} />{!compact && " Sections"}
      </Dropdown.Toggle>
      <Dropdown.Menu className="ph-nav-menu">
        {compact &&
          <>
            <Dropdown.Item href="#top" onClick={(e) => { e.preventDefault(); window.scrollTo({ top: 0, behavior: "smooth" }); }}>
              <ArrowUp size={14} className="align-middle me-2" />Back to top
            </Dropdown.Item>
            <Dropdown.Divider />
          </>
        }
        {sections.map((section) =>
          <Dropdown.Item key={section.id} href={"#" + section.id} onClick={(e) => scrollTo(e, section.id)}>{section.label}</Dropdown.Item>
        )}
      </Dropdown.Menu>
    </Dropdown>
  );
};

// Wraps the full header card; once it scrolls out of view, a slim fixed bar (avatar, title, sections menu) takes its place
export const StickyHeader = ({ avatar, title, subtitle, extra, inactive, children }) => {
  const cardRef = useRef(null);
  const [visible, setVisible] = useState(false);
  const [bounds, setBounds] = useState({ left: 0, width: "100%" });

  useEffect(() => {
    const update = () => {
      const el = cardRef.current;
      if (!el) return;
      const scrolledPast = el.getBoundingClientRect().bottom < 0;
      if (scrolledPast) {
        // Line up with the main content area (it moves with the sidebar)
        const main = document.querySelector(".main");
        if (main) {
          const rect = main.getBoundingClientRect();
          setBounds((prev) => (prev.left === rect.left && prev.width === rect.width ? prev : { left: rect.left, width: rect.width }));
        }
      }
      setVisible(scrolledPast);
    };
    update();
    // Capture phase so scrolling inside an inner container is caught too
    window.addEventListener("scroll", update, true);
    window.addEventListener("resize", update);
    return () => {
      window.removeEventListener("scroll", update, true);
      window.removeEventListener("resize", update);
    };
  }, []);

  return (
    <>
      <div ref={cardRef}>{children}</div>
      <div className={"ph-slim" + (visible ? " ph-slim-visible" : "") + (inactive ? " ph-slim-ended" : "")} style={bounds} aria-hidden={!visible}>
        {avatar}
        <div className="ph-slim-text">
          <span className="ph-slim-title">{title}</span>
          {subtitle && <span className="ph-slim-subtitle">{subtitle}</span>}
        </div>
        <div className="ph-slim-actions">
          {extra}
        </div>
      </div>
    </>
  );
};
